/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Media from 'react-media';
import { withRouter, useHistory } from 'react-router';
import {
    object, shape, array, string, bool, number, func, arrayOf,
} from 'prop-types';
import { compose } from 'recompose';
import { useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/styles';
import { getSSRDeviceType, getVertexAITrackingData } from '../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import { trackPage, coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import GraphqlSearchProducts from './Partials/GraphqlSearchProducts';
import GraphqlSearchErrorPage from './Partials/GraphqlSearchErrorPage';
import GraphqlAfterProductBanner from '../GraphqlCategoryPage/Partials/GraphqlAfterProductBanner/GraphqlAfterProductBanner';
import GraphqlSearchCollections from './Partials/GraphqlSearchCollections';
import GraphqlSearchArticles from './Partials/GraphqlSearchArticles';
import GraphqlSearchFacetFilter from './Partials/GraphqlSearchFacetFilter';
import ProductDesktopFiltersV4 from '../GraphqlCategoryPage/Partials/ProductFacetsV4/ProductDesktopFiltersV4';
import SimpleProductSkeleton from '../GraphqlSkeletonComponents/SimpleProductSkeleton';
import ProductCount from '../ProductFacet/ProductCount';
import SelectedProductFacets from '../ProductFacet/SelectedProductFacets';
import { getFallbackFacetsFromQuery, isFacetApplied, updateFacetQuery } from '../ProductFacet/facetUtils';

const useStyles = makeStyles((theme) => ({
    siteContainer: {
        margin: '0 15px',
        maxWidth: '1400px',
        [theme.breakpoints.up(1430)]: {
            margin: '0 auto',
        },
        [theme.breakpoints.down(336)]: {
            margin: '0 7px',
        },
    },
    header: {
        WebkitFontSmoothing: 'antialiased',
        textAlign: 'left',
        width: '100%',
    },
    numOfResultsContainer: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: '1.2',
        padding: '20px 0',
        width: '100%',
        position: 'relative',
    },
    resultsText: {
        color: theme.palette.colorNeutral70,
        fontFamily: 'Roboto, sans-serif',
        fontSize: '20px',
        letterSpacing: '.03em',
        textShadow: '1px 1px 1px rgba(0, 0, 0, 0.004)',
        padding: '0 10px',
        textOverflow: 'ellipsis',
        overflowX: 'hidden',
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'none',
        },
        [theme.breakpoints.down(600)]: {
            paddingLeft: '10px',
        },
    },
    resultsTextMain: {
        display: 'block',
        width: '100%',
        textAlign: 'center',
        '& h1': {
            margin: '5px 0',
            fontSize: 36,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            lineHeight: 1.5,
        },
        [theme.breakpoints.down(600)]: {
            '& h1': {
                fontSize: 30,
            },
        },
    },
    productBanner: {
        marginTop: '10px',
    },
    displayFlexContainer: {
        width: '100%',
    },
    filterMobile: {
        margin: ' 0 auto',
        marginTop: 15,
    },
    showingResultsFor: {
        fontSize: '18px',
        marginBottom: '5px',
        [theme.breakpoints.up('md')]: {
            fontSize: '25px',
        },
        '&>span': {
            fontWeight: 700,
        },
    },
    originalQuery: {
        fontSize: '14px',
        [theme.breakpoints.up('md')]: {
            fontSize: '16px',
        },
    },
    leftMenu: {
        width: '100%',
        position: 'relative',
        [theme.breakpoints.up(599)]: {
            width: '31.5%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: '99',
        },
        [theme.breakpoints.up(769)]: {
            width: '260px',
            position: 'relative',
        },
        [theme.breakpoints.up(1025)]: {
            zIndex: '9',
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            '-ms-overflow-style': 'none',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
        },
        [theme.breakpoints.down(1025)]: {
            display: 'none',
        },
    },
    productContainer: {
        width: '100%',
        marginLeft: 0,
        [theme.breakpoints.up(1024)]: {
            width: 'calc(100% - 290px)',
            marginLeft: '30px',
        },
    },
    productContainerWithOutFacet: {
        width: '100%',
        marginLeft: 0,
    },
    productCountRoot: {
        height: '44px',
        marginTop: '14px',
    },
}));

function GraphqlSearchPageContainer({
    fetchMoreResults,
    filterOptions,
    loadMoreCount,
    pageCount,
    pageData,
    searchResults,
    searchTerm,
    setFilterOptions,
    track,
    productFacetsGroup,
    collectionData,
    articleData,
    variables,
    prevProductCount,
    productsToLoadOnLoadMoreButton,
    productCount,
    productLoading,
}) {
    const history = useHistory();
    const brand = useSelector(getBrand);
    const device = useSelector(getSSRDeviceType);
    const featureFlags = useSelector(getFeatureFlags);
    const vertexAITrackingData = useSelector(getVertexAITrackingData);

    const [filterData, setFilterData] = useState({});
    const classes = useStyles();

    const setFilterDataInCategoryBuilder = (data, isFacet = false) => {
        if (isFacet) {
            setFilterOptions((prevState) => {
                const nextFilterOptions = { ...prevState };
                if (data?.length) {
                    nextFilterOptions.facets = data;
                } else {
                    delete nextFilterOptions.facets;
                }
                return nextFilterOptions;
            });
        } else {
            setFilterOptions(data);
        }
        setFilterData(data);
    };

    const {
        products, totalProducts, totalPages, experiments = [], attributionToken = '',
    } = pageData;
    const hasProducts = products?.length > 0;
    const facetFilterEnabled = featureFlags['is-search-facet-filter-enabled'];
    let productsPerRow = facetFilterEnabled ? 3 : 4; // leave as default just in case the rest fails
    const productLayout = searchResults?.product_layout || [];
    let j;
    let bannerBlock;
    let bannerPositionTop;
    const categoryBlocks = searchResults?.category_blocks || [];
    categoryBlocks.forEach((block) => {
        if (block.banner) {
            bannerBlock = block.banner;
            bannerPositionTop = bannerBlock?.position === 'Top';
        }
    });
    // loop thru cms data
    for (j = 0; j < productLayout.length; j += 1) {
        if (productLayout[j]?.name?.includes('Desktop')) {
            productsPerRow = productLayout[j]?.products_per_row;
        }
    }
    const updatedExperiments = [...experiments];

    const facetQuery = history?.location?.search || '';
    const fallbackFacets = isFacetApplied(facetQuery) ? getFallbackFacetsFromQuery(facetQuery) : [];
    const updateFallbackFacetQuery = (facets) => {
        history.replace({
            search: updateFacetQuery(facetQuery, facets.map((facet) => facet.value)),
        });
        if (typeof window !== 'undefined') window.location.reload();
    };

    // append vertex-ai experiment if attribution token is present
    if  (attributionToken) {
        experiments.forEach((exp) => {
            updatedExperiments.push({
                ...exp,
                source: 'vertex-ai',
                attributionToken,
                selectedPosition: vertexAITrackingData?.selectedPosition,
                completionAttributionToken: vertexAITrackingData?.completionAttributionToken,
            });
        });
    }

    let productSection = (
        <>
            {fallbackFacets.length > 0 && (
                <SelectedProductFacets
                    selectedFacets={fallbackFacets}
                    onRemove={(value) => updateFallbackFacetQuery(fallbackFacets.filter((facet) => facet.value !== value))}
                    onClearAll={() => updateFallbackFacetQuery([])}
                />
            )}
            <GraphqlSearchErrorPage
                classes={classes}
                products={products}
                filterData={filterData}
                brand={brand}
                pageNumber={pageCount}
                searchTerm={searchTerm}
            />
        </>
    );

    if (productLoading) {
        productSection = <SimpleProductSkeleton />;
    } else if (products !== null && products?.length > 0) {
        productSection = (
            <GraphqlSearchProducts
                brand={brand}
                device={device}
                totalProducts={totalProducts}
                fetchMoreResults={fetchMoreResults}
                loadMoreCount={loadMoreCount}
                pageNumber={pageCount}
                products={products}
                eventSubType="search"
                productsPerRow={productsPerRow}
                searchTerm={searchTerm}
                totalPages={totalPages || 0}
                variables={variables}
                prevProductCount={prevProductCount}
                priceRangeData={productFacetsGroup?.find((ele) => ele?.name === 'Price Range')}
                experiments={updatedExperiments}
                productsToLoadOnLoadMoreButton={productsToLoadOnLoadMoreButton}
                fullWidth
            />
        );
    }

    return (
        <div className={classes.siteContainer}>
            {(bannerBlock && bannerPositionTop) && (
                <Grid className={classes.productBanner}>
                    <GraphqlAfterProductBanner bannerBlock={bannerBlock} trackEvent={track} device={device} />
                </Grid>
            )}
            <Grid className={classes.header}>
                {(hasProducts && facetFilterEnabled && productFacetsGroup.length > 0) ? <GraphqlSearchFacetFilter productFacetsGroup={productFacetsGroup} setFilterData={setFilterDataInCategoryBuilder} productCount={productCount} productLoading={productLoading} searchTerm={decodeURIComponent(searchTerm)} />
                    : (
                        <Media
                            query="(max-width:1024px)"
                            defaultMatches={device !== 'desktop'}
                            render={() => (
                                <div className={classes.productCountRoot}>
                                    <ProductCount productCount={productCount} searchTerm={decodeURIComponent(searchTerm)} />
                                </div>
                            )}
                        />
                    )}
                <Grid container>
                    <Grid item className={hasProducts && facetFilterEnabled && productFacetsGroup.length > 0 ? classes.leftMenu : ''}>
                        {hasProducts && facetFilterEnabled && productFacetsGroup.length > 0 && <GraphqlSearchFacetFilter desktop productFacetsGroup={productFacetsGroup} setFilterData={setFilterDataInCategoryBuilder} productCount={productCount} productLoading={productLoading} searchTerm={decodeURIComponent(searchTerm)} />}
                    </Grid>
                    <Grid item className={hasProducts && facetFilterEnabled && productFacetsGroup.length > 0 ? classes.productContainer : classes.productContainerWithOutFacet} id="productContainer">
                        <Media
                            query="(min-width:1025px)"
                            defaultMatches={device === 'desktop'}
                            render={() => (
                                hasProducts && facetFilterEnabled && productFacetsGroup.length > 0 ? (
                                    <ProductDesktopFiltersV4
                                        productCount={productCount}
                                        productFacets={productFacetsGroup}
                                        loading={productLoading}
                                        setFacetsDataInCollection={(data) => setFilterDataInCategoryBuilder(data, true)}
                                        disableSort
                                        searchPage
                                        searchTerm={decodeURIComponent(searchTerm)}
                                    />
                                ) : (
                                    <div className={classes.productCountRoot}>
                                        <ProductCount productCount={productCount} searchTerm={decodeURIComponent(searchTerm)} />
                                    </div>
                                )
                            )}
                        />
                        {productSection}
                    </Grid>
                </Grid>
                {collectionData?.collections?.length ? (
                    <div>
                        <GraphqlSearchCollections
                            collectionData={collectionData}
                        />
                    </div>
                ) : null}
                {(bannerBlock && !bannerPositionTop) && (
                    <Grid className={classes.productBanner}>
                        <GraphqlAfterProductBanner bannerBlock={bannerBlock} trackEvent={track} device={device} />
                    </Grid>
                )}
                {articleData?.blogs?.length ? (
                    <div>
                        <GraphqlSearchArticles
                            articleData={articleData}
                            searchTerm={searchTerm}
                        />
                    </div>
                ) : null}
            </Grid>
        </div>
    ); // if returned an array with products
}

GraphqlSearchPageContainer.propTypes = {
    searchTerm: string,
    pageData: shape({
        products: array.isRequired,
        redirectUrl: string,
        showingResultsFor: string,
        originalQuery: string,
        experiments: array,
        attributionToken: string,
    }).isRequired,
    loadMoreCount: number,
    pageCount: number.isRequired,
    setFilterOptions: func.isRequired,
    fetchMoreResults: func.isRequired,
    track: func.isRequired,
    searchResults: shape({
        category_blocks: arrayOf(
            shape({
                image: shape({
                    link: shape({
                        href: string.isRequired,
                        title: string.isRequired,
                    }),
                    mobile_image: shape({
                        url: string.isRequired,
                    }),
                }),
            }),
        ),
    }),
    filterOptions: object,
    productFacetsGroup: arrayOf(shape({
        displayOpen: bool,
        name: string,
    })),
    collectionData: shape({
        collections: arrayOf(shape({
            brand: string,
            name: string,
            description: string,
            redirectUrl: string,
            image: string,
            seo: object,
        })),
        total: number,
        pages: number,
    }),
    articleData: shape({
        blogs: arrayOf(shape({
            uid: string,
            title: string,
            excerpt: string,
            image: string,
            contentType: string,
            dateModified: string,
            datePublished: string,
            url: string,
        })),
        total: number,
        pages: number,
    }),
    variables: object,
    prevProductCount: number,
    productsToLoadOnLoadMoreButton: number,
    productCount: number,
    productLoading: bool,
};
GraphqlSearchPageContainer.defaultProps = {
    searchTerm: '',
    searchResults: {},
    loadMoreCount: 1,
    filterOptions: {},
    productFacetsGroup: [],
    collectionData: {},
    articleData: {},
    variables: {},
    prevProductCount: 0,
    productsToLoadOnLoadMoreButton: null,
    productCount: 0,
    productLoading: false,
};

const enhance = compose(
    withRouter,
    trackPage({
        type: 'search',
        name: 'Search',
    }),
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
        updateEveryRender: true,
    }),
);

export default enhance(GraphqlSearchPageContainer);
